import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import _ from 'lodash';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { publish } from '@jutro/events';
import { ADOBE_TOPICS, COMMON_TAGGING_PROPERTIES } from 'saga-modules';
import FormAccordionContent from '../FormAccordionContent/FormAccordionContent';

import metadata from './FormAccordion.metadata.json5';

function FormAccordion(props) {
    const {
        id,
        commonProps,
        onValidate: onValidateParent,
        accordions,
        setShowWizardContinue,
        handleCancel,
        updateProgressBar,
        resetInvalidFields
    } = props;
    const {
        isComponentValid,
        onValidate,
        invalidFields,
        disregardFieldValidation
    } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const [numberOfAccordionsOpen, setNumberOfAccordionsOpen] = useState(0);

    useEffect(() => {
        if (!_.isNil(updateProgressBar)) {
            updateProgressBar(accordions.slice(0, numberOfAccordionsOpen + 1).filter((accordion) => accordion.updatesProgressBar === true).length);
        }
    }, [numberOfAccordionsOpen, accordions]);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        if (resetInvalidFields) {
            const removeFields = invalidFields.filter((field) => field.replace('formAccordionContentComponent', '') > accordions.length - 1);
            disregardFieldValidation(removeFields);
        }
    }, [resetInvalidFields]);

    useEffect(() => {
        if (commonProps.currentStep && commonProps.currentStep.stepProps.isQuoteStep) {
            if (numberOfAccordionsOpen > 0) {
                publish(ADOBE_TOPICS.PREMIUM_ACCORDION, {
                    adobeID: COMMON_TAGGING_PROPERTIES.premiumPageAccordion, value: `continue-${accordions[numberOfAccordionsOpen - 1].name}`
                });
            }
        } else {
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: COMMON_TAGGING_PROPERTIES.quoteSection, value: accordions[numberOfAccordionsOpen].name });
        }
    }, [numberOfAccordionsOpen]);

    const getHeadingClass = useCallback((accordion) => {
        if (accordion.translations && accordion.translations.messages) {
            if (accordion.translations.showClosedHeading !== true) {
                return 'hideClosedHeader';
            }
            return null;
        }
        return 'hideHeader';
    }, []);

    const checkPreviousAccordionsAreValid = useCallback((index) => {
        let valid = true;
        for (let i = 0; i < index; i += 1) {
            if (invalidFields.includes(`formAccordionContentComponent${i}`)) {
                valid = false;
            }
        }
        return valid;
    }, [invalidFields]);

    const minNonPersistentOpenAccordion = useMemo(() => {
        return accordions.findIndex((accordion) => accordion.isOpenAccordionNonPersistent === true);
    }, [accordions]);

    const getAccordionContentOverrides = () => {
        const overrides = [];
        _.each(accordions, (accordion, index) => {
            const objectName = `formAccordionContentComponent${index}`;
            overrides[objectName] = {
                commonProps: commonProps,
                customProps: accordion.customProps,
                onValidate: onValidate,
                hasPreviousAccordionsValid: checkPreviousAccordionsAreValid(index),
                components: accordion.accordionComponents,
                accordionMetadata: accordion.accordionMetadata,
                accordionNumber: index,
                nextButtonId: accordion.nextButtonId,
                accordionTranslations: accordion.translations,
                additionalNextValidation: accordion.additionalNextValidation,
                isLastAccordion: accordions.length === index + 1,
                showNextOnOpen: accordion.showNextOnOpen,
                setShowWizardContinue: setShowWizardContinue,
                numberOfAccordionsOpen: numberOfAccordionsOpen,
                setNumberOfAccordionsOpen: setNumberOfAccordionsOpen,
                handleCancel: handleCancel,
                preventAutoOpen: accordion.preventAutoOpen,
                nextButtonAdditionalText: accordion.nextButtonAdditionalText,
                nextButtonContent: accordion.nextButtonContent,
                showConfirmation: accordion.showConfirmation,
                updatesProgressBar: accordion.updatesProgressBar,
                isOpenAccordionNonPersistent: accordion.isOpenAccordionNonPersistent,
                minNonPersistentOpenAccordion: minNonPersistentOpenAccordion
            };
            const accordionCardName = `card${index}`;
            overrides[accordionCardName] = {
                title: accordion.translations ? translator(accordion.translations.messages, accordion.translations.params) : null,
                isCollapsible: false,
                cardHeadingClassName: getHeadingClass(accordion),
                className: `${getHeadingClass(accordion)} sgFormAccordion`,
            };
            overrides.accordion = {
                showFrame: numberOfAccordionsOpen < index
            };
        });
        return overrides;
    };

    const overrideProps = {
        ...getAccordionContentOverrides(),
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
        },
        resolveComponentMap: {
            FormAccordionContent: FormAccordionContent
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={props}
            showErrors={commonProps.showErrors}
            onValidationChange={onValidate}
            onModelChange={commonProps.updateData}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}
FormAccordion.defaultProps = {
    resetInvalidFields: false
};
export default FormAccordion;
